import React from 'react'
import { Check, X, TrendingUp, TrendingDown } from 'lucide-react'

// 与 MixPlayerPage.tsx 中的适配后数据结构保持一致
interface MixtabilityData {
  bpm: {
    difference_percent: number
    threshold: number
    pass: boolean
  }
  genre: {
    compatibility_score: number
    compatibility_reason: string
    pass: boolean
  }
  score_factors: Array<{
    name: string
    score: number
    threshold: number
    pass: boolean
    description: string
  }>
  overall: {
    pass: boolean
    reason: string
    score: number
  }
}

interface MixtabilityDisplayProps {
  mixtability: MixtabilityData
  statusCode?: number // 添加statusCode参数支持
}

export const MixtabilityDisplay: React.FC<MixtabilityDisplayProps> = ({ mixtability, statusCode }) => {
  const formatScore = (score: number) => score.toFixed(3)
  const formatPercentage = (value: number) => value.toFixed(2)

  return (
    <div className="space-y-6">
      {/* Combined BPM and Score Factors */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Title */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Mixtability</h3>
        </div>
        
        {/* BPM Difference */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            {/* Horizontal Bar Chart - Left Side (Fixed 50% width) */}
            <div className="w-1/2">
              <div className="relative">
                <div className="w-full bg-gray-200 rounded-full h-4 relative overflow-hidden">
                  {/* Score Bar */}
                  <div 
                    className={`h-4 rounded-full transition-all duration-300 ${
                      mixtability.bpm.pass ? 'bg-green-500' : 'bg-yellow-500'
                    }`}
                    style={{ 
                      width: `${Math.min((mixtability.bpm.difference_percent / mixtability.bpm.threshold) * 100, 100)}%` 
                    }}
                  ></div>
                  
                  {/* Threshold Line */}
                  <div 
                    className="absolute top-0 bottom-0 w-0.5 bg-blue-600 border-l-2 border-blue-800"
                    style={{ left: '100%' }}
                  ></div>
                </div>
              </div>
            </div>
            
            {/* BPM Info - Right Side */}
            <div className="flex items-center space-x-4 flex-shrink-0">
              <span className="text-sm font-medium text-gray-900 dark:text-white">BPM差幅</span>
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                mixtability.bpm.pass 
                  ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                  : 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200'
              }`}>
                {mixtability.bpm.pass ? (
                  <>
                    <Check className="h-3 w-3" />
                    <span>通过</span>
                  </>
                ) : (
                  <>
                    <X className="h-3 w-3" />
                    <span>未通过</span>
                  </>
                )}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                差异: {formatPercentage(mixtability.bpm.difference_percent)}% / 阈值: {formatPercentage(mixtability.bpm.threshold)}%
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 dark:border-gray-600 mb-6"></div>

        {/* Score Factors Horizontal Bar Chart */}
        <div className="space-y-3">
          {mixtability.score_factors.map((factor, idx) => (
                        <div key={idx} className="flex items-center justify-between">
              {/* Horizontal Bar Chart - Left Side (Fixed 50% width) */}
              <div className="w-1/2">
                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-4 relative overflow-hidden">
                    {/* Score Bar */}
                    <div 
                      className={`h-4 rounded-full transition-all duration-300 ${
                        factor.pass ? 'bg-green-500' : 'bg-red-500'
                      }`}
                      style={{ 
                        width: `${Math.min((factor.score / Math.max(factor.threshold, 1)) * 100, 100)}%` 
                      }}
                    ></div>
                    
                    {/* Threshold Line */}
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-blue-600 border-l-2 border-blue-800"
                      style={{ 
                        left: `${Math.min((factor.threshold / Math.max(factor.threshold, 1)) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                  
 
                </div>
              </div>
              
              {/* Factor Info - Right Side */}
              <div className="flex items-center space-x-4 flex-shrink-0">
                <span className="text-sm font-medium text-gray-900 dark:text-white min-w-0">{factor.name}</span>
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                  factor.pass 
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                    : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                }`}>
                  {factor.pass ? (
                    <>
                      <Check className="h-3 w-3" />
                      <span>通过</span>
                    </>
                  ) : (
                    <>
                      <X className="h-3 w-3" />
                      <span>未通过</span>
                    </>
                  )}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  得分: {formatScore(factor.score)} / 阈值: {formatScore(factor.threshold)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
